/*
 * Login.jsx
 * ---------
 * Landing page for unauthenticated users.
 * Shows the app name, tagline, sign in button, and guest option.
 * Lists guest limitations clearly so users know what they're missing.
 * Includes a subtle animated background pattern and a Budget app promo.
 * Redirects authenticated users away via App.jsx route guards.
 */

import React, { useState, useEffect } from "react";
import { useAuth } from "../App";

// ============================================
// ANIMATED BACKGROUND
// Subtle floating circles in brand colors
// ============================================
function AnimatedBackground() {
  const circles = [
    { size: 300, x: "-100px", y: "-100px", color: "var(--accent-green)",       opacity: 0.06, duration: 8  },
    { size: 200, x: "70%",    y: "-50px",  color: "var(--accent-gold)",        opacity: 0.05, duration: 10 },
    { size: 150, x: "20%",    y: "60%",    color: "var(--accent-green-light)", opacity: 0.07, duration: 12 },
    { size: 250, x: "80%",    y: "70%",    color: "var(--accent-gold)",        opacity: 0.04, duration: 9  },
    { size: 100, x: "50%",    y: "30%",    color: "var(--accent-green)",       opacity: 0.05, duration: 11 },
  ];

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
      zIndex: 0
    }}>
      {circles.map((circle, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: String(circle.size) + "px",
            height: String(circle.size) + "px",
            borderRadius: "50%",
            background: circle.color,
            opacity: circle.opacity,
            left: circle.x,
            top: circle.y,
            animation: "float " + String(circle.duration) + "s ease-in-out infinite alternate",
            animationDelay: String(i * 0.8) + "s"
          }}
        />
      ))}
    </div>
  );
}

// ============================================
// FEATURE LIST ITEM
// ============================================
function FeatureItem({ icon, text, available }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "var(--space-3)",
      padding: "var(--space-2) 0"
    }}>
      <div style={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        background: available ? "var(--accent-green-muted)" : "var(--bg-surface-2)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}>
        {available ? (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="var(--accent-green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
            <path d="M1 1L7 7M7 1L1 7" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        )}
      </div>
      <span style={{
        fontFamily: "var(--font-body)",
        fontSize: "14px",
        color: available ? "var(--text-primary)" : "var(--text-muted)"
      }}>
        {icon} {text}
      </span>
    </div>
  );
}

// ============================================
// BUDGET APP PROMO
// ============================================
function BudgetPromo() {
  const [hovered, setHovered] = React.useState(false);

  return (<a
    
      href="https://budget.rioscloud.dev"
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-3)",
        background: "var(--bg-surface)",
        border: hovered ? "1px solid var(--accent-gold)" : "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: "var(--space-4)",
        textDecoration: "none",
        boxShadow: hovered ? "var(--shadow-md)" : "none",
        transition: "border-color var(--transition-fast), box-shadow var(--transition-fast)",
        maxWidth: "420px",
        width: "100%"
      }}
    >
      <div style={{
        width: "40px",
        height: "40px",
        borderRadius: "var(--radius-sm)",
        background: "var(--accent-gold-muted)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
        flexShrink: 0
      }}>
        💰
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          fontFamily: "var(--font-body)",
          fontWeight: 600,
          fontSize: "14px",
          color: "var(--text-primary)",
          margin: "0 0 2px"
        }}>
          Also from rioscloud
        </p>
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "13px",
          color: "var(--text-secondary)",
          margin: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}>
          Budget -- Take control of your finances
        </p>
      </div>

      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: "var(--text-muted)", flexShrink: 0 }}>
        <path d="M3 8H13M9 4L13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    </a>
  );
}

// ============================================
// MAIN EXPORT
// ============================================
export default function Login() {
  const { continueAsGuest } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const style = document.createElement("style");
    style.id = "login-float-keyframe";
    style.textContent = `
      @keyframes float {
        from { transform: translateY(0px) scale(1); }
        to   { transform: translateY(-20px) scale(1.05); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      const el = document.getElementById("login-float-keyframe");
      if (el) document.head.removeChild(el);
    };
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg-primary)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: "var(--space-6) var(--space-4)",
      position: "relative",
      overflowX: "hidden"
    }}>
      <AnimatedBackground />

      <div style={{
        position: "relative",
        zIndex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "var(--space-6)",
        width: "100%",
        maxWidth: "420px",
        opacity: mounted ? 1 : 0,
        transform: mounted ? "translateY(0)" : "translateY(24px)",
        transition: "opacity 500ms ease, transform 500ms ease"
      }}>

        {/* Logo + tagline */}
        <div style={{ textAlign: "center" }}>
          <div style={{
            fontFamily: "var(--font-display)",
            fontWeight: 900,
            fontSize: "clamp(48px, 10vw, 72px)",
            color: "var(--accent-green)",
            lineHeight: 1,
            letterSpacing: "-2px",
            marginBottom: "var(--space-3)"
          }}>
            ✓ Checklist
          </div>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "18px",
            color: "var(--text-secondary)",
            margin: 0,
            lineHeight: 1.5
          }}>
            Stay organized. Build habits.
            <br />
            <span style={{ color: "var(--accent-gold)", fontWeight: 600 }}>
              Get things done.
            </span>
          </p>
        </div>

        {/* Sign in card */}
        <div style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-6)",
          width: "100%",
          boxShadow: "var(--shadow-lg)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-4)"
        }}>

          {/* Sign in button */}
          <button
            onClick={() => window.location.href = "/.auth/login/aad"}
            style={{
              width: "100%",
              background: "var(--accent-green)",
              border: "none",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-4)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontWeight: 600,
              fontSize: "16px",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "var(--space-3)",
              transition: "opacity var(--transition-fast)",
              boxShadow: "0 4px 12px rgba(45, 106, 79, 0.3)"
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = "0.9"}
            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="1" y="1" width="8.5" height="8.5" fill="#f25022"/>
              <rect x="10.5" y="1" width="8.5" height="8.5" fill="#7fba00"/>
              <rect x="1" y="10.5" width="8.5" height="8.5" fill="#00a4ef"/>
              <rect x="10.5" y="10.5" width="8.5" height="8.5" fill="#ffb900"/>
            </svg>
            Sign in with Microsoft
          </button>

          {/* Divider */}
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--space-3)"
          }}>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
            <span style={{
              fontFamily: "var(--font-body)",
              fontSize: "13px",
              color: "var(--text-muted)"
            }}>
              or
            </span>
            <div style={{ flex: 1, height: "1px", background: "var(--border)" }} />
          </div>

          {/* Guest button */}
          <button
            onClick={continueAsGuest}
            style={{
              width: "100%",
              background: "none",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)",
              padding: "var(--space-3)",
              cursor: "pointer",
              fontFamily: "var(--font-body)",
              fontWeight: 500,
              fontSize: "15px",
              color: "var(--text-secondary)",
              transition: "border-color var(--transition-fast), background var(--transition-fast)"
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = "var(--border-strong)";
              e.currentTarget.style.background = "var(--bg-surface-2)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = "var(--border)";
              e.currentTarget.style.background = "none";
            }}
          >
            Continue as Guest
          </button>
        </div>

        {/* Feature comparison */}
        <div style={{
          background: "var(--bg-surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-lg)",
          padding: "var(--space-5)",
          width: "100%"
        }}>
          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "12px",
            fontWeight: 600,
            color: "var(--text-muted)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            margin: "0 0 var(--space-3)"
          }}>
            What you get
          </p>

          <FeatureItem icon="📋" text="Unlimited checklists" available={true} />
          <FeatureItem icon="✓"  text="Check off items"      available={true} />
          <FeatureItem icon="🏷️" text="Categories and tags"  available={true} />

          <div style={{
            height: "1px",
            background: "var(--border)",
            margin: "var(--space-3) 0"
          }} />

          <p style={{
            fontFamily: "var(--font-body)",
            fontSize: "11px",
            color: "var(--accent-gold)",
            fontWeight: 600,
            margin: "0 0 var(--space-2)",
            textTransform: "uppercase",
            letterSpacing: "0.05em"
          }}>
            Signed in only
          </p>

          <FeatureItem icon="💾" text="Data saved permanently"  available={false} />
          <FeatureItem icon="🔥" text="Streaks and milestones"  available={false} />
          <FeatureItem icon="📧" text="Email notifications"     available={false} />
          <FeatureItem icon="🔄" text="Recurring checklists"    available={false} />
          <FeatureItem icon="🪞" text="Smart mirror mode"       available={false} />
        </div>

        {/* Budget promo */}
        <BudgetPromo />

        {/* Footer */}
        <p style={{
          fontFamily: "var(--font-body)",
          fontSize: "12px",
          color: "var(--text-muted)",
          textAlign: "center",
          margin: 0
        }}>
          Guest data is cleared every night at 2 AM UTC.
          <br />
          Sign in to keep your data permanently.
        </p>

      </div>
    </div>
  );
}